import { z } from 'zod'

/**
 * Zod-схемы API ответов.
 *
 * Цель — runtime-валидация на границе системы (sync, auth, chat, school),
 * чтобы при изменении backend-контракта фронт падал предсказуемо
 * с понятным сообщением, а не молча через `as unknown as T[]` cast
 * (как раньше в useEntity.ts).
 *
 * Для синхронизируемых сущностей (Student, Grade, …) проверяем только
 * базовые поля (id, updatedAt, deletedAt) — конкретные поля сущности
 * пропускаем через .passthrough(). Это даёт защиту от "backend поменял
 * id на number" без необходимости описывать все 14 типов entity.
 */

/** Минимальная shape для любой sync-entity. */
export const syncEntitySchema = z.object({
  id: z.string(),
  updatedAt: z.number(),
  deletedAt: z.number().nullable().optional(),
}).passthrough()

export const syncPullResponseSchema = z.object({
  records: z.array(syncEntitySchema),
  serverTimestamp: z.number(),
})

export const syncPushResultSchema = z.object({
  accepted: z.number(),
  rejected: z.number(),
  rejectedIds: z.array(z.string()),
  serverTimestamp: z.number(),
})

/** TeacherDto из /auth/me, /auth/login, /auth/register. */
export const teacherDtoSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: z.enum(['TEACHER', 'VICE_PRINCIPAL', 'PRINCIPAL', 'IT_ADMIN', 'ADMIN', 'PARENT', 'SUPER_ADMIN']),
  schoolId: z.string().nullable(),
  locale: z.string(),
  theme: z.string(),
  preferredLlmProvider: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
})

export const tokenResponseSchema = z.object({
  token: z.string(),
  teacher: teacherDtoSchema,
})

/**
 * /auth/login: аутентифицирован, нужен второй фактор (requireTotp) или —
 * 6.1 — 2FA обязательна, но не настроена (mustSetupTotp + ограниченный токен).
 */
export const loginResponseSchema = z.object({
  requireTotp: z.boolean(),
  token: z.string().nullable().optional(),
  teacher: teacherDtoSchema.nullable().optional(),
  challengeToken: z.string().nullable().optional(),
  mustSetupTotp: z.boolean().optional().default(false),
})

export const totpStatusSchema = z.object({
  enabled: z.boolean(),
  hasBackupCodes: z.boolean(),
  required: z.boolean().optional().default(false),
  backupCodesRemaining: z.number().optional().default(0),
})

/** /auth/totp/verify: при настройке с ограниченным токеном — сразу полноценная сессия. */
export const totpVerifyResponseSchema = z.object({
  enabled: z.boolean(),
  teacher: teacherDtoSchema.nullable().optional(),
})

export const backupCodesResponseSchema = z.object({
  backupCodes: z.array(z.string()),
})

export const trustedDeviceSchema = z.object({
  id: z.string(),
  userAgent: z.string().nullable().optional(),
  ipAddr: z.string().nullable().optional(),
  createdAt: z.number(),
  expiresAt: z.number(),
  lastUsedAt: z.number().nullable().optional(),
  isCurrent: z.boolean().optional().default(false),
})

export const totpSetupResponseSchema = z.object({
  secret: z.string(),
  otpauthUrl: z.string(),
  backupCodes: z.array(z.string()),
})

export const sessionDtoSchema = z.object({
  id: z.string(),
  createdAt: z.number(),
  lastSeenAt: z.number(),
  userAgent: z.string().nullable().optional(),
  ipAddr: z.string().nullable().optional(),
  isCurrent: z.boolean(),
})

/** Chat. */
export const chatThreadViewSchema = z.object({
  id: z.string(),
  peerId: z.string(),
  peerName: z.string(),
  studentId: z.string(),
  studentName: z.string(),
  lastMessageAt: z.number(),
  unreadCount: z.number(),
})

export const chatMessageViewSchema = z.object({
  id: z.string(),
  threadId: z.string(),
  senderId: z.string(),
  text: z.string(),
  createdAt: z.number(),
  readAt: z.number().nullable(),
})
